import type { DefaultMenuItem, GetContextMenuItemsParams, IRowNode, MenuItemDef } from 'ag-grid-enterprise'
import consola from 'consola'
import { defineStore } from 'pinia'
import { retry } from 'rxjs'
import { computed, ref, watch } from 'vue'
import { getLastIdByFpnaType } from '~/composables/financials-grid-commons'
import { useAppMachine } from '~/composables/useAppMachine'
import type { GenericPayloadResponse } from '~/protos/gen/stcs_commons.entity'
import { StcsFpnaTypeEnum, StcsFstmtEnum, StcsResCode } from '~/protos/gen/stcs_enums.entity'
import type { StcsI18nLabelMap } from '~/protos/gen/stcs_i18n.entity'
import type { StcsOrgFsdFlat, StcsOrgFsdFlatItem, StcsOrgFsdFlatMeta } from '~/protos/gen/stcs_org_fsd_flat.entity'
import type { DmOrgFsdFlatConfirmRequest, DmOrgFsdFlatSaveDraftRequest } from '~/protos/gen/stcs_wapp_svc_dm.ts'
import { cmwDmOrgFsdFlatConfirmRequest, cmwDmOrgFsdFlatSaveDraftRequest, cmwOnboardingCompleteFsdRequest } from '~/services/cmw.service'
import { buildHierarchy } from '~/services/commons.service'
import type { CustomTreeNode } from '~/stores/acm.store.ts'
import { useCtxStore } from '~/stores/ctx.store'

/**
 * Maximum number of snapshots to keep in undo/redo history.
 * Limiting history prevents unbounded memory growth and improves performance.
 * @constant {number}
 */
const MAX_SNAPSHOT_HISTORY = 5

/**
 * Combined snapshot of all financial statements and i18n data for undo/redo
 */
type FsdSnapshot = {
  pnl: StcsOrgFsdFlatItem[]
  bs: StcsOrgFsdFlatItem[]
  cf: StcsOrgFsdFlatItem[]
  i18nMap: { [key: string]: StcsI18nLabelMap }
}

/**
 * History tracker for undo/redo functionality
 */
type FsdHistory = {
  activeIndex: number
  snapshots: FsdSnapshot[]
}

type LastActionFsdList = FsdHistory

export enum Step {
  EDIT = 0,
  CONFIRM = 1,
  CONFIRM_LOADING = 2,
}

/**
 * Financial statement level counts structure
 */
export interface OrgFsdMetricsItem {
  /** Count of level 2 items */
  level_2_count: number
  /** Count of level 1 items */
  level_1_count: number
  /** Count of sub-total items */
  level_sub_total_count: number
  /** Count of total items */
  level_total_count: number
}

/**
 * Organization financial statement metrics structure
 */
export interface OrgFsdMetrics {
  /** Profit & Loss statement counts */
  pnl: OrgFsdMetricsItem
  /** Balance Sheet statement counts */
  bs: OrgFsdMetricsItem
  /** Cash Flow statement counts */
  cf: OrgFsdMetricsItem
}

export const useFsdStore = defineStore(
  'fsd-store',
  () => {
    const fstmtPnlList = ref<StcsOrgFsdFlatItem[]>([])
    const fstmtBsList = ref<StcsOrgFsdFlatItem[]>([])
    const fstmtCfList = ref<StcsOrgFsdFlatItem[]>([])
    const fsdMeta = ref<StcsOrgFsdFlatMeta>()
    const lastActionFsdList = ref<LastActionFsdList>()
    const fsdCopy = ref<StcsOrgFsdFlat>(Object.assign({} as StcsOrgFsdFlat, {}))
    const isdraft = ref(false)
    const isDataReady = ref(false)
    const mode = ref<Step | undefined>(Step.EDIT)
    const fsdMetrics = ref<OrgFsdMetrics>({
      pnl: { level_1_count: 0, level_2_count: 0, level_sub_total_count: 0, level_total_count: 0 },
      bs: { level_1_count: 0, level_2_count: 0, level_sub_total_count: 0, level_total_count: 0 },
      cf: { level_1_count: 0, level_2_count: 0, level_sub_total_count: 0, level_total_count: 0 },
    })

    // Watcher for financial statement list changes
    watch(
      [fstmtPnlList, fstmtBsList, fstmtCfList],
      ([newPnl, newBs, newCf]) => {
        fsdMetrics.value = {
          pnl: calculateStatementCounts(newPnl),
          bs: calculateStatementCounts(newBs),
          cf: calculateStatementCounts(newCf),
        }
      },
      { deep: true, immediate: true },
    )

    const locale = useAppMachine().locale
    const { $tolgee } = useNuxtApp()

    const fstmtPnlOptionMap = ref<{ [key: string]: StcsOrgFsdFlatItem }>({})
    const fstmtBsOptionMap = ref<{ [key: string]: StcsOrgFsdFlatItem }>({})
    const fstmtBsToCfMap = ref<{ [key: string]: { parent: string; id: string } }>({})
    const fstmtPnlToCfMap = ref<{ [key: string]: { parent: string; id: string } }>({})

    const i18nMap = ref<{ [key: string]: StcsI18nLabelMap }>({})
    const displayNameMap = computed<Record<string, string>>(() => i18nMap.value[locale.value]?.label_map ?? {})
    const alternateDisplayNameMap = computed<Record<string, string>>(() => i18nMap.value[locale.value === 'en' ? 'de' : 'en']?.label_map ?? {})

    const activeFinancialStatement = ref<StcsFstmtEnum>(StcsFstmtEnum.SFSE_PNL)

    function isFinancialStatementActive(finStmt: StcsFstmtEnum): boolean {
      return activeFinancialStatement.value === finStmt
    }

    function setFinancialStatement(finStmt: StcsFstmtEnum) {
      if (activeFinancialStatement.value !== finStmt) {
        activeFinancialStatement.value = finStmt
      }
    }

    async function updateFsdData(successCb?: () => void, errorCb?: () => void, updateLastAction = true) {
      // Add null check for fsdMeta.value
      if (!fsdMeta.value) {
        return
      }

      if (updateLastAction) {
        updateLastActionFsdList()
      }

      isdraft.value = true
      fsdMeta.value.is_custom = true
      const updateRequest: DmOrgFsdFlatSaveDraftRequest = {
        ref_id_user: useCtxStore().ref_user.id.extractId(),
        ref_id_org: useCtxStore().active_org_id.extractId(),
        obj_org_fsd_flat: {
          id: fsdCopy.value.id,
          meta: fsdCopy.value.meta,
          version: fsdCopy.value.version,
          pnl_list: fstmtPnlList.value,
          bs_list: fstmtBsList.value,
          cf_list: fstmtCfList.value,
          i18n_map: i18nMap.value,
          i18n_custom_key: fsdCopy.value.i18n_custom_key ?? useCtxStore().active_org_id.extractId(),
          is_draft: true,
        },
      }
      isdraft.value = true
      consola.debug('updateRequest: ', updateRequest)

      cmwDmOrgFsdFlatSaveDraftRequest(updateRequest)
        .pipe(retry(3))
        .subscribe((response: GenericPayloadResponse) => {
          consola.debug('cmwOrgFsdFlatUpdateRequest: ', response)
          switch (response.res_code) {
            case StcsResCode.SUCCESS: {
              useCtxStore().clearKpiCacheForOrg(useCtxStore().active_org_id)
              successCb?.()
              break
            }
            default:
              errorCb?.()
              break
          }
        })
    }

    function onConfirmFsdData(isOnBoarding = true, successCb?: (response: GenericPayloadResponse) => void, errorCb?: () => void) {
      const updateRequest: DmOrgFsdFlatConfirmRequest = {
        ref_id_user: useCtxStore().ref_user.id.extractId(),
        ref_id_org: useCtxStore().active_org_id.extractId(),
      }
      consola.debug('onConfirmFsdData: updateRequest', updateRequest)

      isdraft.value = false

      const request = isOnBoarding ? cmwOnboardingCompleteFsdRequest : cmwDmOrgFsdFlatConfirmRequest

      request(updateRequest).subscribe((response: GenericPayloadResponse) => {
        consola.debug('completeFsdRequest: ', response)
        switch (response.res_code) {
          case StcsResCode.SUCCESS: {
            useCtxStore().clearKpiCacheForOrg(useCtxStore().active_org_id)
            successCb?.(response)
            break
          }
          default:
            errorCb?.()
            break
        }
      })
    }

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
    function setFSDStatementLists(fsd: StcsOrgFsdFlat | undefined) {
      if (!fsd) {
        return
      }
      fsdCopy.value = fsd
      fsdMeta.value = fsd.meta
      isdraft.value = fsd.is_draft

      fstmtPnlList.value = fsd?.pnl_list ?? []
      const pnlOptionsList: StcsOrgFsdFlatItem[] = [
        {
          fstmt_type: 1,
          fpna_type: 4,
          level_id: 'level_1_revenues',
          rowClass: 'account-mapping-l1',
          options: {
            invert_sum_value: -1,
          },
          row_id: '',
          path_level: [],
        },
        {
          fstmt_type: 1,
          fpna_type: 4,
          level_id: 'level_1_output',
          rowClass: 'account-mapping-l1',
          options: {
            invert_sum_value: -1,
          },
          row_id: '',
          path_level: [],
        },
        {
          fstmt_type: 1,
          fpna_type: 4,
          level_id: 'level_1_direct_costs',
          rowClass: 'account-mapping-l1',
          options: {
            invert_sum_value: -1,
          },
          path_level: [],
          row_id: '',
        },
        {
          fstmt_type: 1,
          fpna_type: 4,
          level_id: 'level_1_operating_expenditure',
          rowClass: 'account-mapping-l1',
          options: {
            invert_sum_value: -1,
          },
          row_id: '',
          path_level: [],
        },
        {
          fstmt_type: 1,
          fpna_type: 4,
          level_id: 'level_1_other_operating_income',
          rowClass: 'account-mapping-l1',
          options: {
            invert_sum_value: -1,
          },
          row_id: '',
          path_level: [],
        },
        {
          fstmt_type: 1,
          fpna_type: 4,
          level_id: 'level_1_dna',
          rowClass: 'account-mapping-l1',
          options: {
            filter_on_zero_sum_period: false,
            invert_sum_value: -1,
          },
          row_id: '',
          path_level: [],
        },
        {
          fstmt_type: 1,
          fpna_type: 4,
          level_id: 'level_1_net_interest',
          rowClass: 'account-mapping-l1',
          options: {
            filter_on_zero_sum_period: false,
            invert_sum_value: -1,
          },
          row_id: '',
          path_level: [],
        },
        {
          fstmt_type: 1,
          fpna_type: 4,
          level_id: 'level_1_other_income_or_loss',
          rowClass: 'account-mapping-l1',
          options: {
            filter_on_zero_sum_period: true,
            invert_sum_value: -1,
          },
          row_id: '',
          path_level: [],
        },
        {
          fstmt_type: 1,
          fpna_type: 4,
          level_id: 'level_1_non_recurring_items',
          rowClass: 'account-mapping-l1',
          options: {
            filter_on_zero_sum_period: true,
            invert_sum_value: -1,
          },
          row_id: '',
          path_level: [],
        },
        {
          fstmt_type: 1,
          fpna_type: 4,
          level_id: 'level_1_tax_expense',
          rowClass: 'account-mapping-l1',
          options: {
            filter_on_zero_sum_period: false,
            invert_sum_value: -1,
          },
          row_id: '',
          path_level: [],
        },
      ]

      // Create option map from existing FSD items, maintaining order from pnlOptionsList
      const pnlOptionsFromFsd: { [key: string]: StcsOrgFsdFlatItem } = {}

      // First, collect all existing items from FSD into a temporary map
      const existingPnlItems = new Map<string, StcsOrgFsdFlatItem>()
      fstmtPnlList.value
        .filter((item) => pnlOptionsList.some((suffix) => item.row_id.endsWith(suffix.level_id) && !item?.ref_id_mirror))
        .forEach((item) => {
          existingPnlItems.set(getLastIdByFpnaType(item.row_id, StcsFpnaTypeEnum.SFT_LEVEL_1), item)
        })

      // Build the option map in the order defined by pnlOptionsList
      // This ensures consistent ordering and includes stubs for missing defaults
      for (const pnlTemplateItem of pnlOptionsList) {
        const existingItem = existingPnlItems.get(pnlTemplateItem.level_id)
        if (existingItem) {
          // Use existing item from FSD
          pnlOptionsFromFsd[pnlTemplateItem.level_id] = existingItem
        } else {
          // Create a stub item for missing default level 1 item
          pnlOptionsFromFsd[pnlTemplateItem.level_id] = {
            ...pnlTemplateItem,
            row_id: `default_${pnlTemplateItem.level_id}`,
            path_level: [`default_${pnlTemplateItem.level_id}`],
          }
        }
      }
      fstmtPnlOptionMap.value = pnlOptionsFromFsd

      fstmtBsList.value = fsd?.bs_list ?? []
      const bsOptionsList: StcsOrgFsdFlatItem[] = [
        {
          fstmt_type: 2,
          fpna_type: 4,
          level_id: 'level_1_fixed_assets',
          rowClass: 'account-mapping-l1',
          options: {
            filter_on_zero_sum_period: false,
            active_fstmt_side: true,
            invert_sum_value: 1,
          },
          row_id: '',
          path_level: [],
        },
        {
          fstmt_type: 2,
          fpna_type: 4,
          level_id: 'level_1_inventory',
          rowClass: 'account-mapping-l1',
          options: {
            active_fstmt_side: true,
            invert_sum_value: 1,
          },
          row_id: '',
          path_level: [],
        },
        {
          fstmt_type: 2,
          fpna_type: 4,
          level_id: 'level_1_trade_receivables',
          rowClass: 'account-mapping-l1',
          options: {
            filter_on_zero_sum_period: false,
            active_fstmt_side: true,
            invert_sum_value: 1,
          },
          row_id: '',
          path_level: [],
        },
        {
          fstmt_type: 2,
          fpna_type: 4,
          level_id: 'level_1_other_assets',
          rowClass: 'account-mapping-l1',
          options: {
            filter_on_zero_sum_period: false,
            active_fstmt_side: true,
            invert_sum_value: 1,
          },
          row_id: '',
          path_level: [],
        },
        {
          fstmt_type: 2,
          fpna_type: 4,
          level_id: 'level_1_equity',
          rowClass: 'account-mapping-l1',
          options: {
            filter_on_zero_sum_period: false,
            invert_sum_value: -1,
          },
          row_id: '',
          path_level: [],
        },
        {
          fstmt_type: 2,
          fpna_type: 4,
          level_id: 'level_1_provisions',
          rowClass: 'account-mapping-l1',
          options: {
            filter_on_zero_sum_period: false,
            invert_sum_value: -1,
          },
          row_id: '',
          path_level: [],
        },
        {
          fstmt_type: 2,
          fpna_type: 4,
          level_id: 'level_1_financial_liabilities',
          rowClass: 'account-mapping-l1',
          options: {
            filter_on_zero_sum_period: false,
            active_fstmt_side: false,
            invert_sum_value: -1,
          },
          row_id: '',
          path_level: [],
        },
        {
          fstmt_type: 2,
          fpna_type: 4,
          level_id: 'level_1_trade_payables',
          rowClass: 'account-mapping-l1',
          options: {
            filter_on_zero_sum_period: false,
            active_fstmt_side: false,
            invert_sum_value: -1,
          },
          row_id: '',
          path_level: [],
        },
        {
          fstmt_type: 2,
          fpna_type: 4,
          level_id: 'level_1_other_liabilities',
          rowClass: 'account-mapping-l1',
          options: {
            filter_on_zero_sum_period: false,
            active_fstmt_side: false,
            invert_sum_value: -1,
          },
          row_id: '',
          path_level: [],
        },
      ]

      // Create option map from existing FSD items, maintaining order from bsOptionsList
      const bsOptionsFromFsd: { [key: string]: StcsOrgFsdFlatItem } = {}

      // First, collect all existing items from FSD into a temporary map
      const existingBsItems = new Map<string, StcsOrgFsdFlatItem>()
      fstmtBsList.value
        .filter((item) => bsOptionsList.some((suffix) => item.row_id.endsWith(suffix.level_id) && !item?.ref_id_mirror))
        .forEach((item) => {
          existingBsItems.set(getLastIdByFpnaType(item.row_id, StcsFpnaTypeEnum.SFT_LEVEL_1), item)
        })

      // Build the option map in the order defined by bsOptionsList
      // This ensures consistent ordering and includes stubs for missing defaults
      for (const bsTemplateItem of bsOptionsList) {
        const existingItem = existingBsItems.get(bsTemplateItem.level_id)
        if (existingItem) {
          // Use existing item from FSD
          bsOptionsFromFsd[bsTemplateItem.level_id] = existingItem
        } else {
          // Create a stub item for missing default level 1 item
          bsOptionsFromFsd[bsTemplateItem.level_id] = {
            ...bsTemplateItem,
            row_id: `default_${bsTemplateItem.level_id}`,
            path_level: [`default_${bsTemplateItem.level_id}`],
          }
        }
      }
      fstmtBsOptionMap.value = bsOptionsFromFsd

      fstmtCfList.value = fsd?.cf_list ?? []
      buildRowToSubtotalMap(fstmtCfList.value)

      i18nMap.value = fsd?.i18n_map ?? {}

      // Initialize undo/redo history with first snapshot
      lastActionFsdList.value = {
        activeIndex: 0,
        snapshots: [
          {
            pnl: JSON.parse(JSON.stringify(fstmtPnlList.value)),
            bs: JSON.parse(JSON.stringify(fstmtBsList.value)),
            cf: JSON.parse(JSON.stringify(fstmtCfList.value)),
            i18nMap: JSON.parse(JSON.stringify(i18nMap.value)),
          },
        ],
      }
    }

    function buildRowToSubtotalMap(list: StcsOrgFsdFlatItem[]) {
      for (const row of list) {
        switch (row.fpna_type) {
          case StcsFpnaTypeEnum.SFT_LEVEL_1:
            if (row?.ref_id_mirror) {
              fstmtBsToCfMap.value[row?.ref_id_mirror] = {
                parent: row.path_level[0],
                id: row.row_id,
              }
            } else {
              fstmtBsToCfMap.value[row.row_id] = {
                parent: row.path_level[0],
                id: row.row_id,
              }
            }
            break
          case StcsFpnaTypeEnum.SFT_LEVEL_2: {
            if (row?.ref_id_mirror) {
              fstmtBsToCfMap.value[row?.ref_id_mirror] = {
                parent: row.path_level[1],
                id: row.row_id,
              }
            } else {
              fstmtBsToCfMap.value[row.row_id] = {
                parent: row.path_level[1],
                id: row.row_id,
              }
            }
            break
          }
          default: {
            break
          }
        }
      }

      return fstmtBsToCfMap.value
    }

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
    function getDisplayNameBylevelId(item: StcsOrgFsdFlatItem): { name: string; isAlternate: boolean } {
      // Return default display name if item is undefined
      if (!item) {
        return { name: $tolgee.t('not_available'), isAlternate: false }
      }

      let displayName = { name: displayNameMap.value[item?.row_id], isAlternate: false }

      if (!displayName.name && alternateDisplayNameMap.value[item?.row_id]) {
        displayName.name = alternateDisplayNameMap.value[item?.row_id]
        displayName.isAlternate = true
      } else if (!displayName.name) {
        displayName.name = displayNameMap.value[item?.level_id] || $tolgee.t('not_available')
      }

      const nameMap = displayName.isAlternate ? alternateDisplayNameMap.value : displayNameMap.value
      const prefix = item?.ref_id_prefix ? nameMap[item?.ref_id_prefix] : undefined

      displayName.name = prefix ? `${prefix} ${displayName.name}` : displayName.name

      if (item?.ref_id_mirror) {
        displayName = processMirrorReference(item)
      }

      if (item.fpna_type === StcsFpnaTypeEnum.SFT_CTX_LINE_IPO || item.fpna_type === StcsFpnaTypeEnum.SFT_CTX_LINE_GROWTH) {
        const levelName = nameMap[item?.level_id]
        const refName = item?.ref_id_mirror ? displayName : nameMap[item?.sum_ref ?? '']

        displayName.name = item.fpna_type === StcsFpnaTypeEnum.SFT_CTX_LINE_GROWTH ? levelName : `${levelName} ${refName}`
      }

      return displayName
    }

    function processMirrorReference(item: StcsOrgFsdFlatItem, isAlternate = false): { name: string; isAlternate: boolean } {
      const ref_id_mirror = item.ref_id_mirror ?? ''
      let displayName = { name: displayNameMap.value[ref_id_mirror], isAlternate }

      if (!displayName.name && alternateDisplayNameMap.value[ref_id_mirror]) {
        displayName.name = alternateDisplayNameMap.value[ref_id_mirror]
        displayName.isAlternate = true
      } else if (!displayName.name) {
        displayName.name = displayNameMap.value[item?.level_id] || $tolgee.t('not_available')
      }

      const nameMap = displayName.isAlternate ? alternateDisplayNameMap.value : displayNameMap.value

      const prefix = item.ref_id_prefix ? nameMap[item.ref_id_prefix] : undefined

      displayName = findRootDisplayName(ref_id_mirror ?? item?.level_id, displayName.isAlternate)

      displayName.name = prefix ? `${prefix} ${displayName.name}` : displayName.name

      return displayName
    }

    function findRootDisplayName(ref_id: string, isAlternate = false): { name: string; isAlternate: boolean } {
      const nameMap = isAlternate ? alternateDisplayNameMap.value : displayNameMap.value

      if (nameMap[ref_id]) {
        return { name: nameMap[ref_id], isAlternate }
      }

      const item = [...fstmtPnlList.value, ...fstmtBsList.value, ...fstmtCfList.value].find((item) => item.row_id === ref_id)

      if (item) {
        return processMirrorReference(item, isAlternate)
      }

      return { name: $tolgee.t('not_available'), isAlternate }
    }

    function updateDisplayName(node: IRowNode, _oldDisplayName: string, newDisplayName: string) {
      const ref_id = node.data.ref_id_mirror ?? node.data.row_id

      if (displayNameMap.value[ref_id]) {
        delete alternateDisplayNameMap.value[ref_id]
      }

      updateI18nMap(ref_id, newDisplayName)

      // Update in PNL statement
      const ref_id_level_id = getLastIdByFpnaType(ref_id, StcsFpnaTypeEnum.SFT_LEVEL_1)
      fstmtPnlList.value = updateDisplayNameInList(fstmtPnlList.value, ref_id, newDisplayName)

      // Update in BS statement
      fstmtBsList.value = updateDisplayNameInList(fstmtBsList.value, ref_id, newDisplayName)

      // Update options in PNL and BS statements
      if (node.data.fpna_type === StcsFpnaTypeEnum.SFT_LEVEL_1 && !node.data?.ref_id_mirror) {
        if (ref_id_level_id in fstmtPnlOptionMap.value) {
          updateI18nMap(ref_id_level_id, newDisplayName)
        }

        if (ref_id_level_id in fstmtBsOptionMap.value) {
          updateI18nMap(ref_id_level_id, newDisplayName)
        }
      }

      // Update in CF statement
      fstmtCfList.value = updateDisplayNameInList(fstmtCfList.value, ref_id, newDisplayName)
    }

    function updateDisplayNameInList(list: StcsOrgFsdFlatItem[], ref_id: string, newDisplayName: string) {
      list.forEach((item: StcsOrgFsdFlatItem) => {
        if (item.row_id === ref_id) {
          updateI18nMap(ref_id, newDisplayName)
        }
      })
      return list
    }

    /**
     * Add a row to the specified financial statement at the given index
     * @param row The row data to insert
     * @param insertIndex The index at which to insert the row (defaults to end of array if invalid)
     * @param financialStatement The financial statement to add the row to
     * @param relativeToRowId Optional row ID to insert relative to (before or after)
     * @param insertAfter If relativeToRowId is provided, whether to insert after (true) or before (false)
     */
    function addRow(row: StcsOrgFsdFlatItem, financialStatement: StcsFstmtEnum, relativeToRowId?: string, insertIndex = -1, insertAfter = false) {
      const listRefs: Record<StcsFstmtEnum.SFSE_PNL | StcsFstmtEnum.SFSE_BS | StcsFstmtEnum.SFSE_CF, Ref<StcsOrgFsdFlatItem[]>> = {
        [StcsFstmtEnum.SFSE_PNL]: fstmtPnlList,
        [StcsFstmtEnum.SFSE_BS]: fstmtBsList,
        [StcsFstmtEnum.SFSE_CF]: fstmtCfList,
      }

      // Get the target list reference and value
      const targetRef = listRefs[financialStatement as StcsFstmtEnum.SFSE_PNL | StcsFstmtEnum.SFSE_BS | StcsFstmtEnum.SFSE_CF]
      if (!targetRef?.value) {
        return
      }

      const targetList = targetRef.value
      const rowExists = targetList.some((item) => item.row_id === row.row_id)

      if (rowExists) {
        return
      }
      // Determine insertion index
      let finalInsertIndex = insertIndex

      if (relativeToRowId) {
        const relativeIndex = targetList.findIndex((item) => item.row_id === relativeToRowId)
        if (relativeIndex !== -1) {
          finalInsertIndex = insertAfter ? relativeIndex + 1 : relativeIndex
        }
      }

      // Ensure index is valid (if negative or too large)
      finalInsertIndex = finalInsertIndex < 0 ? targetList.length : finalInsertIndex
      finalInsertIndex = Math.min(finalInsertIndex, targetList.length)

      // Update the list with the new row inserted
      const newArr = [...targetList]
      newArr.splice(finalInsertIndex, 0, row)
      targetRef.value = newArr
    }

    /**
     * Delete a row from the financial statements and its references
     * E.g.: this.fsdStore.deleteRows(draggedNode.data.row_id, StcsFstmtEnum.SFSE_BS, [StcsFstmtEnum.SFSE_CF])
     * @param targetId
     * @param arrays
     * @param currentFinancialStatement
     * @param arraysToSearch
     */
    function deleteCascadingReferences(targetId: string, arrays: { [key in StcsFstmtEnum]: StcsOrgFsdFlatItem[] }, currentFinancialStatement?: StcsFstmtEnum, arraysToSearch?: StcsFstmtEnum[]) {
      const arrayNames = arraysToSearch !== undefined && arraysToSearch.length > 0 ? arraysToSearch : [StcsFstmtEnum.SFSE_PNL, StcsFstmtEnum.SFSE_BS, StcsFstmtEnum.SFSE_CF]

      // Set to track IDs that should be deleted
      const idsToDelete = new Set()

      // Recursive function to find all references
      function findReferencesToDelete(id: string) {
        // Find all items that reference this id
        arrayNames.forEach((arrayName: StcsFstmtEnum) => {
          let searchForMirrorItem = true
          if (currentFinancialStatement === arrayName) {
            // If we provide a financial statement, then DO NOT search for a mirror item within its array because it would delete mirror L2 items
            searchForMirrorItem = false
          }
          arrays[arrayName].forEach((item) => {
            const searchCondition = searchForMirrorItem ? item?.ref_id_mirror === id || item.row_id === id : item.row_id === id
            if (searchCondition && !idsToDelete.has(item.row_id)) {
              // Mark this item for deletion
              idsToDelete.add(item.row_id)
              // Recursively find items that reference this item
              findReferencesToDelete(item.row_id)
            }
          })
        })
      }

      // Start the recursive search with our target ID
      findReferencesToDelete(targetId)

      consola.debug('idsToDelete: ', idsToDelete)
      // Create new arrays without the deleted items
      const result: { [key: string]: StcsOrgFsdFlatItem[] } = { ...arrays }
      arrayNames.forEach((arrayName) => {
        result[arrayName] = arrays[arrayName].filter((item) => !idsToDelete.has(item.row_id))
      })

      return result
    }

    function deleteRows(id: string, currentFinancialStatement?: StcsFstmtEnum, arraysToSearch?: StcsFstmtEnum[]) {
      if (!id) {
        return
      }
      const result = deleteCascadingReferences(
        id,
        {
          '-1': [],
          '0': [],
          [StcsFstmtEnum.SFSE_PNL]: fstmtPnlList.value,
          [StcsFstmtEnum.SFSE_BS]: fstmtBsList.value,
          [StcsFstmtEnum.SFSE_CF]: fstmtCfList.value,
        },
        currentFinancialStatement,
        arraysToSearch,
      )

      fstmtPnlList.value = result[StcsFstmtEnum.SFSE_PNL]
      fstmtBsList.value = result[StcsFstmtEnum.SFSE_BS]
      fstmtCfList.value = result[StcsFstmtEnum.SFSE_CF]
    }

    function updateStatement(newData: StcsOrgFsdFlatItem[], financialStatement: StcsFstmtEnum) {
      switch (financialStatement) {
        case StcsFstmtEnum.SFSE_PNL:
          fstmtPnlList.value = newData
          break
        case StcsFstmtEnum.SFSE_BS:
          fstmtBsList.value = newData
          break
        case StcsFstmtEnum.SFSE_CF:
          fstmtCfList.value = newData
          break
        default:
          break
      }

      fstmtPnlList.value = reorderFinancialStatementItems([...fstmtPnlList.value])
      fstmtBsList.value = reorderFinancialStatementItems([...fstmtBsList.value])
      fstmtCfList.value = reorderFinancialStatementItems([...fstmtCfList.value])
    }

    /**
     * Creates an optimized snapshot of all financial statements and i18nMap for undo/redo functionality.
     * This function saves the complete state including PnL, BS, CF, and i18nMap together.
     *
     * Performance optimizations:
     * - Limits history to MAX_SNAPSHOT_HISTORY items to prevent unbounded memory growth
     * - Efficiently manages snapshot buffer by removing oldest snapshots when limit is reached
     * - Removes any "future" snapshots when creating a new branch in history
     */
    function updateLastActionFsdList() {
      if (!lastActionFsdList.value) {
        return
      }

      // Create a snapshot of all financial statements and i18nMap
      const snapshot: FsdSnapshot = {
        pnl: JSON.parse(JSON.stringify(fstmtPnlList.value)),
        bs: JSON.parse(JSON.stringify(fstmtBsList.value)),
        cf: JSON.parse(JSON.stringify(fstmtCfList.value)),
        i18nMap: JSON.parse(JSON.stringify(i18nMap.value)),
      }

      // Remove any snapshots after the current index (for branching history)
      const currentSnapshots = lastActionFsdList.value.snapshots.slice(0, lastActionFsdList.value.activeIndex + 1)

      // Add the new snapshot
      currentSnapshots.push(snapshot)

      // Enforce maximum history limit: keep only the most recent MAX_SNAPSHOT_HISTORY snapshots
      // This prevents unbounded memory growth and improves performance
      if (currentSnapshots.length > MAX_SNAPSHOT_HISTORY) {
        // Remove oldest snapshots, keeping the most recent ones
        const startIndex = currentSnapshots.length - MAX_SNAPSHOT_HISTORY
        lastActionFsdList.value.snapshots = currentSnapshots.slice(startIndex)
        // Adjust activeIndex to account for removed snapshots
        lastActionFsdList.value.activeIndex = MAX_SNAPSHOT_HISTORY - 1
      } else {
        lastActionFsdList.value.snapshots = currentSnapshots
        lastActionFsdList.value.activeIndex = currentSnapshots.length - 1
      }
    }

    /**
     * Undo the last action by restoring the previous snapshot.
     * This restores all financial statements (PnL, BS, CF) and i18nMap together.
     */
    function undoLastAction() {
      if (!lastActionFsdList.value) {
        return
      }

      const activeIndex = lastActionFsdList.value.activeIndex
      if (activeIndex <= 0) {
        return
      }

      // Move to the previous snapshot
      lastActionFsdList.value.activeIndex--
      const previousSnapshot = lastActionFsdList.value.snapshots[lastActionFsdList.value.activeIndex]

      // Restore all financial statements and i18nMap from the snapshot
      i18nMap.value = previousSnapshot.i18nMap
      fstmtPnlList.value = previousSnapshot.pnl
      fstmtBsList.value = previousSnapshot.bs
      fstmtCfList.value = previousSnapshot.cf

      updateFsdData(
        () => null,
        () => null,
        false,
      )
    }

    /**
     * Redo the last undone action by restoring the next snapshot.
     * This restores all financial statements (PnL, BS, CF) and i18nMap together.
     */
    function redoLastAction() {
      if (!lastActionFsdList.value) {
        return
      }

      const activeIndex = lastActionFsdList.value.activeIndex
      if (activeIndex >= lastActionFsdList.value.snapshots.length - 1) {
        return
      }

      // Move to the next snapshot
      lastActionFsdList.value.activeIndex++
      const nextSnapshot = lastActionFsdList.value.snapshots[lastActionFsdList.value.activeIndex]

      // Restore all financial statements and i18nMap from the snapshot
      i18nMap.value = nextSnapshot.i18nMap
      fstmtPnlList.value = nextSnapshot.pnl
      fstmtBsList.value = nextSnapshot.bs
      fstmtCfList.value = nextSnapshot.cf

      updateFsdData(
        () => null,
        () => null,
        false,
      )
    }

    /**
     * Replaces all occurrences of a parent ID with a new ID across all financial statements and related mappings.
     *
     * This method efficiently updates ID references throughout the financial data structure, including:
     * - All three financial statement types (Profit & Loss, Balance Sheet, Cash Flow)
     * - Display name mappings (i18n)
     * - Balance Sheet to Cash Flow mappings
     *
     * The function performs ID replacement on multiple properties of financial statement items:
     * - `row_id`: The primary identifier of the financial statement item
     * - `ref_id_mirror`: Reference to mirrored items across statements
     * - `sum_ref`: Reference used for summation calculations
     * - `path_level`: Hierarchical path array containing parent-child relationships
     *
     * @param {string} parentId - The original parent ID to be replaced (e.g., 'pnl_total_a')
     * @param {string} newId - The new ID to replace the parent ID with (e.g., 'pnl_total_a_total_hashId')
     * @param {boolean} rename_same_id - Whether to rename items that exactly match the parentId
     *   - `true`: Renames items with row_id exactly matching parentId
     *   - `false`: Skips items with row_id exactly matching parentId or ctx_line variations
     * @param {boolean} [exact_match=false] - Determines the matching strategy for ID replacement
     *   - `true`: Only replaces IDs that exactly match the parentId
     *   - `false`: Replaces IDs that start with or contain the parentId (prefix/substring matching)
     *
     * @example
     * ```typescript
     * // Replace exact matches only, rename same IDs
     * replaceIdInFinancialStatements('revenue_total', 'revenue_total_new', true, true);
     *
     * // Replace all IDs starting with prefix, skip exact matches
     * replaceIdInFinancialStatements('pnl_section_', 'pnl_updated_section_', false, false);
     *
     * // Common use case: Creating new totals while preserving ID structure
     * replaceIdInFinancialStatements('pnl_total_a', 'pnl_total_a_total_123abc', false, false);
     * ```
     *
     * @remarks
     * **Performance Optimizations:**
     * - Pre-computes condition flags to reduce repeated calculations
     * - Uses single-pass iteration through all financial statements
     * - Batches map updates to avoid modification during iteration
     * - Employs early exit strategies for skip conditions
     *
     * **Data Structure Updates:**
     * 1. **Financial Statement Items**: Updates all matching items across PnL, BS, and CF statements
     * 2. **Display Name Map**: Updates internationalization keys and display names
     * 3. **Balance Sheet to Cash Flow Map**: Updates cross-statement reference mappings
     *
     * **Matching Behavior:**
     * - When `exact_match=true`: Only items with IDs exactly equal to `parentId` are updated
     * - When `exact_match=false`: Items with IDs starting with `parentId` (prefix match) or containing `parentId` in path_level are updated
     *
     * **Skip Conditions:**
     * - When `rename_same_id=false`: Items with `row_id` exactly matching `parentId` or `${parentId}_ctx_line` are skipped
     *
     * @throws {Error} Implicitly throws if financial statement references are undefined
     *
     * @since 1.0.0
     * @version 2.0.0 - Optimized for performance with single-pass processing
     *
     * @see {@link useFsdStore} - Store containing financial statement data
     * @see {@link StcsFstmtEnum} - Enum defining statement types (ProfitLoss, BalanceSheet, CashFlow)
     */
    //biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Refactor when possible
    function replaceIdInFinancialStatements(parentId: string, newId: string, rename_same_id: boolean, exact_match?: boolean): void {
      // Pre-compute conditions to avoid repeated calculations
      const shouldSkipCtxLine = !rename_same_id
      const shouldSkipParentId = !rename_same_id

      // Helper function to check if replacement is needed
      const shouldReplace = exact_match ? (id: string) => id === parentId : (id: string) => id.startsWith(parentId)

      // Helper function to check path inclusion
      const shouldReplacePath = exact_match ? (path: string) => path === parentId : (path: string) => path.includes(parentId)

      // Process all three statement types in a single loop
      const allStatements = [fstmtPnlList.value, fstmtBsList.value, fstmtCfList.value]

      // Single pass through all statements
      for (const statementData of allStatements) {
        for (const item of statementData) {
          // Early skip conditions with pre-computed flags
          if (shouldSkipCtxLine && item.row_id === `${parentId}_ctx_line`) {
            continue
          }
          if (shouldSkipParentId && item.row_id === parentId) {
            continue
          }

          // Update row_id
          if (shouldReplace(item.row_id)) {
            item.row_id = item.row_id.replace(parentId, newId)
          }

          // Update ref_id_mirror (with optional chaining for safety)
          if (item?.ref_id_mirror && shouldReplace(item.ref_id_mirror)) {
            item.ref_id_mirror = item.ref_id_mirror.replace(parentId, newId)
          }

          // Update sum_ref
          if (item.sum_ref && shouldReplace(item.sum_ref)) {
            item.sum_ref = item.sum_ref.replace(parentId, newId)
          }

          // Update path_level (only if it exists)
          if (item.path_level?.length > 0) {
            item.path_level = item.path_level.map((path) => (shouldReplacePath(path) ? path.replace(parentId, newId) : path))
          }
        }
      }

      // Batch process maps to minimize object operations
      const mapsToUpdate = [
        { map: { ...displayNameMap.value, ...alternateDisplayNameMap.value }, name: 'displayNameMap' },
        { map: fstmtBsToCfMap.value, name: 'fstmtBsToCfMap' },
      ]

      const entriesToUpdate: [string, string, string][] = []

      for (const { map } of mapsToUpdate) {
        // Collect entries to update first to avoid modifying during iteration
        for (const [oldId, value] of Object.entries(map)) {
          if (shouldReplace(oldId)) {
            const newItemId = oldId.replace(parentId, newId)
            entriesToUpdate.push([oldId, newItemId, value])
          }
        }

        // Apply updates in batch
        for (const [oldId, newItemId, value] of entriesToUpdate) {
          delete map[oldId]
          map[newItemId] = value
        }
      }

      // Update i18nMap: rename IDs inside any locale's label_map
      for (const [_, localeData] of Object.entries(i18nMap.value)) {
        if (localeData?.label_map) {
          // Collect all i18nMap entries that need updating
          const i18nEntriesToUpdate: [string, string][] = []
          for (const oldId of Object.keys(localeData.label_map)) {
            if (shouldReplace(oldId)) {
              const newItemId = oldId.replace(parentId, newId)
              // Only add if the ID actually changed
              if (oldId !== newItemId) {
                i18nEntriesToUpdate.push([oldId, newItemId])
              }
            }
          }

          // First add all new entries
          for (const [oldId, newItemId] of i18nEntriesToUpdate) {
            localeData.label_map[newItemId] = localeData.label_map[oldId]
          }

          // Then remove all old entries
          for (const [oldId] of i18nEntriesToUpdate) {
            delete localeData.label_map[oldId]
          }
        }
      }
    }

    /**
     * Remove duplicate financial statements based on (parentId and newId) path_level and (ref_id_mirror)
     */
    function removeFSDDuplicates(): void {
      // Process all three statement types
      const allStatements = [
        { data: fstmtPnlList.value, type: StcsFstmtEnum.SFSE_PNL },
        { data: fstmtBsList.value, type: StcsFstmtEnum.SFSE_BS },
        { data: fstmtCfList.value, type: StcsFstmtEnum.SFSE_CF },
      ]

      // Process each statement type
      for (const statement of allStatements) {
        const uniqueIds = new Set()
        const filteredData = statement.data
          .filter((item) => {
            // Check if the item is a duplicate based on row_id or ref_id_mirror
            const isDuplicate = uniqueIds.has(item.row_id)
            if (!isDuplicate) {
              uniqueIds.add(item.row_id)
              if (item?.ref_id_mirror) {
                uniqueIds.add(item.ref_id_mirror)
              }
            }
            return !isDuplicate
          })
          .map((item) => {
            const selfReferenced = item.row_id === item?.ref_id_mirror
            if (selfReferenced) {
              const refItem = statement.data.find((i) => i.row_id === item.row_id && i?.ref_id_mirror === undefined)
              return refItem ? { ...refItem, ref_id_mirror: undefined } : { ...item, ref_id_mirror: undefined }
            }
            return item
          })
        // Reorder the filtered data
        statement.data.splice(0, statement.data.length, ...filteredData)
      }
    }

    /**
     * Reorders the items in the financial statement based on the path_level and fpna_type
     *
     enum StcsFpnaTypeEnum {
     SFT_ENUM_NONE = 0; // None
     SFT_TOTAL_CML = 1; // Total CML, this can be a parent of other Totals or Sub Totals
     SFT_TOTAL_NCML = 2; // Total NCML
     SFT_SUB_TOTAL = 3; // Sub Total, can be skipped sometimes, meaning that Total has no sub total as child but only level 1 childs
     SFT_LEVEL_1 = 4; // Level 1, child of a Total or Sub Total
     SFT_LEVEL_2 = 5; // Level 2, child of a Level 1
     SFT_LEVEL_3 = 6; // Level 3, child of a Level 2, not used in FSD
     SFT_ACC_ITEM = 7; // Account Item, child of a Level 3, not used in FSD
     SFT_CTX_LINE_IPO = 8; // Total Context Line IPO, assistant element for Totals, always below a Total
     SFT_CTX_LINE_GROWTH = 9; // Total Context Line Growth, assistant element for Totals, always below a Total
     }
     * @param items
     * @returns
     */
    function reorderFinancialStatementItems(items: StcsOrgFsdFlatItem[]): StcsOrgFsdFlatItem[] {
      const originalIndexMap: { [key: string]: number } = {}
      items.forEach((item, index) => {
        originalIndexMap[item.row_id] = index
      })

      const contextLinesOutput = new Set<string>()
      const processedIds = new Set<string>()

      // Helper: recursively stack items (Post-order traversal approach)
      function stackByDepth(parent: StcsOrgFsdFlatItem): StcsOrgFsdFlatItem[] {
        // Check if already processed
        if (processedIds.has(parent.row_id)) {
          return []
        }
        // Mark as processed immediately upon entry
        processedIds.add(parent.row_id)

        // Find direct children (items whose path_level is parent's + 1 level)
        const children = items.filter((i) => {
          return (
            i.row_id !== parent.row_id && // Not the same item
            i.path_level &&
            parent.path_level &&
            i.path_level.length === parent.path_level.length + 1 &&
            i.path_level[i.path_level.length - 1] === i.row_id &&
            parent.path_level.every((p, idx) => p === i.path_level[idx])
          )
        })

        // Sort children: Primarily by fpna_type descending (5->4->3->...), then by original index ascending
        children.sort((a, b) => {
          // Primary sort: fpna_type descending (higher number = deeper level, should come first)
          const typeOrder = (b.fpna_type || 0) - (a.fpna_type || 0)
          if (typeOrder !== 0) {
            return typeOrder
          }

          // Secondary sort: original index ascending
          return (originalIndexMap[a.row_id] ?? Number.POSITIVE_INFINITY) - (originalIndexMap[b.row_id] ?? Number.POSITIVE_INFINITY)
        })

        let childrenResult: StcsOrgFsdFlatItem[] = []
        for (const child of children) {
          // No processedIds check here, rely on the check at the start of the recursive call
          childrenResult = childrenResult.concat(stackByDepth(child))
        }

        // Parent is already marked as processed
        // Result: All processed children first, then the parent
        const result = [...childrenResult, parent]

        // Add context lines immediately after their total/subtotal parent
        const contextLines = items.filter(
          (i) => (i.fpna_type === 8 || i.fpna_type === 9) && i.path_level && i.path_level.length > 0 && (i.row_id === `${parent.row_id}_ctx_line` || i.path_level[0] === parent.row_id) && !contextLinesOutput.has(i.row_id), // Check contextLinesOutput to prevent duplicates if handled by multiple parents?
          // No need to check processedIds for context lines here, as they are added directly
        )
        contextLines.sort((a, b) => (originalIndexMap[a.row_id] ?? Number.POSITIVE_INFINITY) - (originalIndexMap[b.row_id] ?? Number.POSITIVE_INFINITY))

        if (contextLines.length > 0) {
          result.push(...contextLines)
          contextLines.forEach((ctx) => {
            if (!processedIds.has(ctx.row_id)) {
              processedIds.add(ctx.row_id) // Mark context lines as processed
            }
            contextLinesOutput.add(ctx.row_id) // Track output to prevent double adding
          })
        }

        return result
      }

      // Find all potential root items
      const potentialRoots = items.filter((i) => {
        if (!i.path_level) {
          return false // Need path_level
        }
        // Strict roots (length 1, not context)
        if (i.path_level.length === 1 && i.fpna_type !== 8 && i.fpna_type !== 9) {
          return true
        }
        // Potential nested roots (Totals/SubTotals not already processed and without a clear parent in the list)
        if (i.fpna_type === 1 || i.fpna_type === 2 || i.fpna_type === 3) {
          const hasParentInList = items.some(
            (p) => p.path_level && i.path_level && p.row_id !== i.row_id && i.path_level.length === p.path_level.length + 1 && i.path_level.every((pathSegment, idx) => (idx < p.path_level.length ? pathSegment === p.path_level[idx] : true)),
          )
          return !hasParentInList
        }
        return false
      })

      // Specific IDs from the failing test
      const net_income_level_id = 'total_net_income'
      const net_income_row_id = items.find((item) => item.row_id.endsWith(net_income_level_id))?.row_id || 'row_id_not_found_vBC3'
      const ebt_row_level_id = 'total_ebt'
      const ebt_row_id = items.find((item) => item.row_id.endsWith(ebt_row_level_id))?.row_id || 'row_id_not_found_vBC4'

      // log error if net_income_row_id || ebt_row_id contains not found
      if (net_income_row_id.includes('not_found') || ebt_row_id.includes('not_found')) {
        consola.warn('Could not find net_income_row_id or ebt_row_id in items, using fallback sorting')
      }

      // Sort roots: Special case for the known problematic pair, otherwise use original index
      potentialRoots.sort((a, b) => {
        const idA = a.row_id
        const idB = b.row_id

        // Specific rule: Ensure EBT group comes before Net Income group if both are roots
        if (idA === ebt_row_id && idB === net_income_row_id) {
          return -1
        }
        if (idA === net_income_row_id && idB === ebt_row_id) {
          return 1
        }

        // Fallback to original index
        return (originalIndexMap[idA] ?? Number.POSITIVE_INFINITY) - (originalIndexMap[idB] ?? Number.POSITIVE_INFINITY)
      })

      // Stack starting from the determined roots
      let finalResult: StcsOrgFsdFlatItem[] = []
      for (const root of potentialRoots) {
        if (!processedIds.has(root.row_id)) {
          finalResult = finalResult.concat(stackByDepth(root))
        }
      }

      // Add any items potentially missed (orphans)
      const remainingItems = items.filter((item) => !processedIds.has(item.row_id))
      remainingItems.sort((a, b) => (originalIndexMap[a.row_id] ?? Number.POSITIVE_INFINITY) - (originalIndexMap[b.row_id] ?? Number.POSITIVE_INFINITY))
      finalResult = finalResult.concat(remainingItems)

      return finalResult
    }

    function getContextMenuOptions(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] | Promise<(DefaultMenuItem | MenuItemDef)[]> {
      const buildStatementMenuItems = (list: StcsOrgFsdFlatItem[]) => buildMenuFromTree(buildHierarchy(list), params)

      return [
        {
          name: $tolgee.t('in-percent-of'),
          subMenu: [
            {
              name: $tolgee.t('general-terms-pnl'),
              subMenu: buildStatementMenuItems(fstmtPnlList.value),
            },
            {
              name: $tolgee.t('general-terms-bs'),
              subMenu: buildStatementMenuItems(fstmtBsList.value),
            },
            {
              name: $tolgee.t('general-terms-cf'),
              subMenu: buildStatementMenuItems(fstmtCfList.value),
            },
          ],
        },
        {
          name: $tolgee.t('growth'),
          action: () => {
            handlePercentReferenceSelection(params.node?.data, undefined)
          },
        },
      ]
    }

    function buildMenuFromTree(nodes: CustomTreeNode[], params: GetContextMenuItemsParams): MenuItemDef[] {
      return nodes
        .filter((node) => {
          return ![StcsFpnaTypeEnum.SFT_CTX_LINE_IPO, StcsFpnaTypeEnum.SFT_CTX_LINE_GROWTH].includes(node.data.fpna_type)
        })
        .map((node) => {
          const menuItem: MenuItemDef = {
            name: node.label || processMirrorReference(node.data).name,
          }

          const TOTAL_TYPES = new Set([StcsFpnaTypeEnum.SFT_TOTAL_CML, StcsFpnaTypeEnum.SFT_TOTAL_NCML])

          if (TOTAL_TYPES.has(node.data.fpna_type)) {
            menuItem.cssClasses = ['font-bold']
          }

          if (node.children.length > 0 && [StcsFpnaTypeEnum.SFT_LEVEL_1, StcsFpnaTypeEnum.SFT_SUB_TOTAL].includes(node.data.fpna_type)) {
            menuItem.subMenu = buildMenuFromTree(node.children, params)
          }

          menuItem.action = () => {
            handlePercentReferenceSelection(params.node?.data, node.data)
          }

          return menuItem
        })
    }

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
    function handlePercentReferenceSelection(ctxNode: StcsOrgFsdFlatItem, reference: StcsOrgFsdFlatItem | undefined) {
      if (reference) {
        const rowInStore = fstmtPnlList.value?.find((r: StcsOrgFsdFlatItem) => r.row_id === ctxNode?.row_id)
        if (rowInStore) {
          rowInStore.level_id = 'ctx_line_ipo'
          const rowIdInLabelMap = displayNameMap.value[reference.row_id]
          const levelIdInLabelMap = displayNameMap.value[reference.level_id]
          rowInStore.sum_ref = rowIdInLabelMap ? reference.row_id : levelIdInLabelMap ? reference.level_id : reference.row_id
          rowInStore.ref_id_mirror = reference?.ref_id_mirror
          rowInStore.ref_id_prefix = reference?.ref_id_prefix
          rowInStore.sum_ref = reference.row_id
          rowInStore.fpna_type = StcsFpnaTypeEnum.SFT_CTX_LINE_IPO
        }
      } else {
        const rowInStore = fstmtPnlList.value?.find((r: StcsOrgFsdFlatItem) => r.row_id === ctxNode.row_id)
        if (rowInStore) {
          rowInStore.level_id = 'ctx_line_growth'
          rowInStore.sum_ref = undefined
          rowInStore.ref_id_mirror = undefined
          rowInStore.ref_id_prefix = undefined
          rowInStore.fpna_type = StcsFpnaTypeEnum.SFT_CTX_LINE_GROWTH
        }
      }
      updateFsdData()
    }

    function updateI18nMap(ref_id: string, displayName: string) {
      const currentLocale = locale.value

      if (!i18nMap.value[currentLocale]) {
        i18nMap.value[currentLocale] = { label_map: {} }
      }
      if (!i18nMap.value[currentLocale].label_map) {
        i18nMap.value[currentLocale].label_map = {}
      }

      i18nMap.value[currentLocale].label_map[ref_id] = displayName
    }

    // Function to calculate counts for a statement
    function calculateStatementCounts(fstmt_items: StcsOrgFsdFlatItem[]): OrgFsdMetricsItem {
      // Check for any occurrence of "level_3_non_categorized" in the fstmt_items
      return {
        level_2_count: fstmt_items.filter((item) => item.fpna_type === StcsFpnaTypeEnum.SFT_LEVEL_2).length,
        level_1_count: fstmt_items.filter((item) => item.fpna_type === StcsFpnaTypeEnum.SFT_LEVEL_1).length,
        level_sub_total_count: fstmt_items.filter((item) => item.fpna_type === StcsFpnaTypeEnum.SFT_SUB_TOTAL).length,
        level_total_count: fstmt_items.filter((item) => item.fpna_type === StcsFpnaTypeEnum.SFT_TOTAL_CML || item.fpna_type === StcsFpnaTypeEnum.SFT_TOTAL_NCML).length,
      }
    }

    return {
      activeFinancialStatement,
      addRow,
      deleteRows,
      displayNameMap,
      fsdMeta,
      fsdMetrics,
      fstmtBsList,
      fstmtBsOptionMap,
      fstmtBsToCfMap,
      fstmtPnlToCfMap,
      fstmtCfList,
      fstmtPnlList,
      fstmtPnlOptionMap,
      getContextMenuOptions,
      getDisplayNameBylevelId,
      i18nMap,
      isFinancialStatementActive,
      isdraft,
      isDataReady,
      lastActionFsdList,
      mode,
      onConfirmFsdData,
      processMirrorReference,
      redoLastAction,
      removeFSDDuplicates,
      reorderFinancialStatementItems,
      replaceIdInFinancialStatements,
      setFSDStatementLists,
      setFinancialStatement,
      undoLastAction,
      updateDisplayName,
      updateFsdData,
      updateI18nMap,
      updateStatement,
    }
  },
  { persist: false },
)
